/**
 * UrLatnNastaliqToast
 *
 * One-time informational toast shown when a Roman-Urdu (`ur-Latn`) viewer first
 * encounters dynamic content. The hook silently fetches Urdu Nastaliq script for
 * dynamic content (no production-grade Roman-Urdu translation provider exists),
 * so the static UI stays in their picked locale while the campaign body shifts
 * to Nastaliq. This toast sets that expectation honestly.
 *
 * Persistence: localStorage flag `amalq:urLatn:nastaliq:seen` — once dismissed
 * or shown, never again on this device.
 *
 * Renders nothing if the user is not on `ur-Latn` or if they've already seen it.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, X } from 'lucide-react';

const STORAGE_KEY = 'amalq:urLatn:nastaliq:seen';

export const UrLatnNastaliqToast: React.FC = () => {
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (i18n.language !== 'ur-Latn') return;
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(STORAGE_KEY) === '1') return;
    // Brief delay so it doesn't fight the page-load animations
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [i18n.language]);

  const handleDismiss = (): void => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, '1');
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] bg-white border border-emerald-100 shadow-xl shadow-emerald-500/10 rounded-2xl p-4 pr-3 flex items-start gap-3"
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center">
        <Languages className="w-4 h-4 text-emerald-600" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 mb-0.5">
          Translated content shows in Urdu Nastaliq
        </p>
        <p className="text-xs text-gray-600 leading-relaxed">
          Your interface stays in Roman Urdu (your pick). Dynamic content like
          campaign stories is shown in Urdu script — no production-grade Roman
          Urdu translation exists yet.
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 p-1 rounded-md hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-emerald-500"
        aria-label="Dismiss notice"
      >
        <X className="w-4 h-4 text-gray-500" aria-hidden />
      </button>
    </div>
  );
};

export default UrLatnNastaliqToast;
